import { Link, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import SitterSelectStep from './components/SitterSelectStep';
import ServiceDateStep, { BookingFormData } from './components/ServiceDateStep';
import ConfirmPayStep from './components/ConfirmPayStep';

const STEPS = ['シッターを選ぶ', 'サービス・日程', '確認・送信'];
const FORM_URL = 'https://readdy.ai/api/form/d6osqvmancq2eie824j0';

const emptyForm: BookingFormData = {
  service: '',
  startDate: '',
  endDate: '',
  petType: '',
  petDetails: '',
  notes: '',
  name: '',
  email: '',
};

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get('sitterId') ? Number(searchParams.get('sitterId')) : null;

  const [step, setStep] = useState<1 | 2 | 3>(preselectedId ? 2 : 1);
  const [selectedSitterId, setSelectedSitterId] = useState<number | null>(preselectedId);
  const [formData, setFormData] = useState<BookingFormData>(emptyForm);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleFormChange = (data: Partial<BookingFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const reset = () => {
    setStep(1);
    setSelectedSitterId(null);
    setFormData(emptyForm);
    setSubmitSuccess(false);
  };

  return (
    <div className="min-h-screen bg-[#EEF4F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#81D8D0] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-24 sm:h-28">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg"
                alt="PawBridge"
                className="w-40 h-40 sm:w-48 sm:h-48 object-contain brightness-0"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8 lg:gap-10">
              <Link to="/about" className="relative text-sm font-semibold text-white/90 hover:text-white transition-colors whitespace-nowrap group">
                PawBridgeについて
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitters" className="relative text-sm font-semibold text-white/90 hover:text-white transition-colors whitespace-nowrap group">
                シッターを探す
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitter-registration" className="relative text-sm font-semibold text-white/90 hover:text-white transition-colors whitespace-nowrap group">
                シッター登録
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/login" className="px-6 py-2.5 bg-white text-[#81D8D0] text-sm font-bold rounded-full hover:bg-[#1B2A4A] hover:text-white transition-all whitespace-nowrap">ログイン</Link>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-1 border-t border-white/20 pt-3">
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg">PawBridgeについて</Link>
              <Link to="/sitters" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg">シッターを探す</Link>
              <Link to="/sitter-registration" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg">シッター登録</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg">ログイン</Link>
            </div>
          )}
        </div>
      </nav>

      <div className="pt-32 pb-16 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mb-2">予約リクエスト</h1>
          </div>

          {/* Success State */}
          {submitSuccess ? (
            <div className="bg-white rounded-2xl p-8 sm:p-12 text-center">
              <div className="w-20 h-20 bg-[#EEF4F7] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-send-plane-fill text-4xl text-[#81D8D0]"></i>
              </div>
              <h2 className="text-2xl font-bold text-[#1B2A4A] mb-3">リクエストを送りました！</h2>
              <p className="text-sm text-[#1B2A4A]/60 mb-6 leading-relaxed">
                シッターがリクエストを確認し、承認または辞退の返答をします。<br />
                結果はメールでお知らせします。
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8 text-left">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-timer-line text-amber-500 text-lg"></i>
                  <h3 className="text-sm font-bold text-amber-800">承認後のお支払いについて</h3>
                </div>
                <ul className="text-xs text-amber-700 space-y-2 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                    <span>シッターが承認した場合のみ、お支払いが発生します</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-time-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                    <span>承認のご連絡から <strong>48時間以内</strong> にお支払いをお願いします</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-mail-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                    <span>お支払いリンクは承認メールに記載されます</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#EEF4F7] rounded-xl p-5 mb-8 text-left">
                <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3">キャンセルポリシー</h3>
                <ul className="text-xs text-[#1B2A4A]/60 space-y-2">
                  <li className="flex items-start gap-2"><i className="ri-check-line text-green-600 mt-0.5"></i><span>予約日の48時間前まで：無料キャンセル可能</span></li>
                  <li className="flex items-start gap-2"><i className="ri-information-line text-[#81D8D0] mt-0.5"></i><span>24時間以内：お支払い額の50%を請求</span></li>
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/" className="px-6 py-3 bg-[#EEF4F7] text-[#1B2A4A] border border-[#D4E8EC] rounded-xl hover:bg-[#D4E8EC] transition-colors whitespace-nowrap text-center">ホームに戻る</Link>
                <button onClick={reset} className="px-6 py-3 bg-[#81D8D0] text-white rounded-xl hover:bg-[#1B2A4A] transition-colors whitespace-nowrap cursor-pointer">別の予約をする</button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {/* Step Indicator */}
              <div className="px-6 sm:px-8 pt-6 pb-5 border-b border-[#EEF4F7]">
                <div className="flex items-center gap-0">
                  {STEPS.map((label, i) => {
                    const num = i + 1;
                    const isDone = step > num;
                    const isActive = step === num;
                    return (
                      <div key={num} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                            isDone ? 'bg-[#81D8D0] text-white' :
                            isActive ? 'bg-[#1B2A4A] text-white' :
                            'bg-[#EEF4F7] text-[#1B2A4A]/40'
                          }`}>
                            {isDone ? <i className="ri-check-line text-sm"></i> : num}
                          </div>
                          <span className={`text-xs font-medium whitespace-nowrap ${isActive ? 'text-[#1B2A4A]' : 'text-[#1B2A4A]/40'}`}>
                            {label}
                          </span>
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`flex-1 h-0.5 mx-2 mb-5 transition-all ${step > num ? 'bg-[#81D8D0]' : 'bg-[#EEF4F7]'}`}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Step Content */}
              <div className="p-6 sm:p-8">
                {step === 1 && (
                  <SitterSelectStep
                    selectedSitterId={selectedSitterId}
                    onSelect={id => setSelectedSitterId(id)}
                    onNext={() => setStep(2)}
                  />
                )}
                {step === 2 && (
                  <ServiceDateStep
                    selectedSitterId={selectedSitterId}
                    formData={formData}
                    onChange={handleFormChange}
                    onNext={() => setStep(3)}
                    onBack={() => setStep(1)}
                  />
                )}
                {step === 3 && (
                  <ConfirmPayStep
                    selectedSitterId={selectedSitterId}
                    formData={formData}
                    onBack={() => setStep(2)}
                    onSuccess={() => setSubmitSuccess(true)}
                    formAction={FORM_URL}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
